// ============================================================
// COVER, WHEN TWO THINGS CLAIM TO PROVIDE IT.
//
// A combatant can have cover from two places at once: a value pinned by
// hand on the sheet, and whatever the map works out from where they are
// standing. Until v8.13.0 the pin simply DELETED the map's answer -
// "5 cover to the legs" against a wall giving full cover left the legs
// protected and the wall thrown away entirely.
//
// That was an accepted decision ("a hand-pinned ruling wins over the
// map") and it is wrong in the common case, because the map already
// applies a different rule to itself: among several solids in the line,
// THE HIGHEST APPLIES AND THEY ARE NEVER ADDED. The pin should have
// been one more candidate in that comparison all along.
//
// Tables disagree about the rest of it, so this is a ruling rather than
// a fact, and the GM picks. Pure. Zero React dependency.
// ============================================================
import { COVER_ORDER } from './battlefield/lineOfSight';
import type { Cover } from '../types';

export type CoverConflictMode = 'highest' | 'best' | 'location' | 'pinned';

export interface CoverConflictEntry {
  key: CoverConflictMode;
  label: string;
  hint: string;
}

/** The rulings on offer, in the order the GM sees them. */
export const COVER_CONFLICT_MODES: CoverConflictEntry[] = [
  {
    key: 'highest',
    label: 'The highest applies',
    hint: 'A pinned value and the map are two candidates and the better one wins, exactly as the map already decides between several walls. Cover is never added together. A tie goes to the pin.',
  },
  {
    key: 'best',
    label: 'The highest, at the best armour',
    hint: 'The wider cover decides how much of them is hidden, and the better of the two AP values decides how hard they are to hurt - a man behind a wall carrying a shield gets the wall and the shield. Generous: a shield that only covered the legs lends its armour to a head shot.',
  },
  {
    key: 'location',
    label: 'The best for each body part',
    hint: 'Every location takes whichever cover actually protects it, at that cover’s own armour. Legs behind a shield keep the shield’s AP while the rest of them take the wall’s. The most faithful, and the only one where "5 cover to the legs" means exactly that.',
  },
  {
    key: 'pinned',
    label: 'A pinned value wins outright',
    hint: 'What the app did before v8.13.0: any cover set by hand overrides the map completely. A defensible ruling - "I have decided what cover this man has" - and here so that no table’s numbers move without them choosing it.',
  },
];

const KEYS = new Set(COVER_CONFLICT_MODES.map((m) => m.key));

/** A stored ruling is untrusted input - a later build may have written a mode this one lacks. */
export function isCoverConflictMode(value: unknown): value is CoverConflictMode {
  return typeof value === 'string' && KEYS.has(value as CoverConflictMode);
}

export interface ResolvedCover {
  /** What `target.cover` becomes. */
  cover: Cover;
  /**
   * The OTHER cover, kept only for `location`.
   *
   * Cover is one `{level, ap}` everywhere else in the app, and the level
   * is what decides which body parts it reaches. Resolving per location
   * therefore cannot be done here - it needs the hit - so both survive
   * to `damageCalc`, which asks each of them about the part that was
   * actually struck.
   */
  alt?: Cover;
}

const rank = (c: Cover | null | undefined) => COVER_ORDER[c?.level ?? 'none'];

/**
 * Which cover a combatant has, given a pin and whatever the map says.
 *
 * A tie goes to the PIN in every mode. It is a ruling somebody made on
 * purpose, and the map's answer changes every time anybody moves.
 */
export function resolveCoverConflict(
  manual: Cover | null | undefined,
  derived: Cover | null | undefined,
  mode: CoverConflictMode = 'highest',
): ResolvedCover {
  if (!derived || derived.level === 'none') return { cover: (manual ?? { level: 'none', ap: 0 }) as Cover };
  if (!manual || manual.level === 'none') return { cover: derived };
  return (RESOLVERS[mode] ?? RESOLVERS.highest)(manual, derived);
}

/** The pin and the map both say something; each mode decides between them. */
const RESOLVERS: Record<CoverConflictMode, (manual: Cover, derived: Cover) => ResolvedCover> = {
  pinned: (manual) => ({ cover: manual }),
  best: (manual, derived) => ({
    cover: { ...wider(manual, derived), ap: Math.max(manual.ap ?? 0, derived.ap ?? 0) },
  }),
  // Both survive; `damageCalc` asks each about the part that was hit.
  location: (manual, derived) => {
    const cover = wider(manual, derived);
    return { cover, alt: cover === derived ? manual : derived };
  },
  highest: (manual, derived) => ({ cover: wider(manual, derived) }),
};

/** The cover reaching more of the body; a tie goes to the pin. */
const wider = (manual: Cover, derived: Cover): Cover => (rank(derived) > rank(manual) ? derived : manual);
